'use client'

import { useState, useCallback } from 'react'
import { NotificationData, NotificationType } from '@/components/NotificationPopup'

export function useNotificationPopups() {
  const [notifications, setNotifications] = useState<NotificationData[]>([])

  const addNotification = useCallback((
    type: NotificationType,
    title: string,
    message?: string,
    options?: {
      duration?: number
      actions?: Array<{
        label: string
        onClick: () => void
        variant?: 'primary' | 'secondary'
      }>
    }
  ) => {
    const id = Math.random().toString(36).substring(2) + Date.now().toString(36)
    
    const notification: NotificationData = {
      id,
      type,
      title,
      message,
      duration: options?.duration ?? (type === 'error' ? 0 : 5000), // Errors persist, others auto-dismiss after 5s
      actions: options?.actions
    }

    setNotifications(prev => [...prev, notification])
    return id
  }, [])

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  const clearAllNotifications = useCallback(() => {
    setNotifications([])
  }, [])

  // Convenience methods
  const success = useCallback((title: string, message?: string, options?: Parameters<typeof addNotification>[3]) => 
    addNotification('success', title, message, options), [addNotification])

  const error = useCallback((title: string, message?: string, options?: Parameters<typeof addNotification>[3]) => 
    addNotification('error', title, message, options), [addNotification])

  const warning = useCallback((title: string, message?: string, options?: Parameters<typeof addNotification>[3]) => 
    addNotification('warning', title, message, options), [addNotification])

  const info = useCallback((title: string, message?: string, options?: Parameters<typeof addNotification>[3]) => 
    addNotification('info', title, message, options), [addNotification])

  return {
    notifications,
    addNotification,
    dismissNotification,
    clearAllNotifications,
    success,
    error,
    warning,
    info
  }
}